import React, { useState } from "react"
import { Link } from "react-router-dom"
import theme from "../../assets/images/login_img_new.png"
import logo from "../../assets/images/logo.png"
import { useFormik } from "formik"
import { toast } from "react-toastify"
toast.configure()
import "react-toastify/dist/ReactToastify.css"
import { RequestState } from "enum/RequestState"
import { loginSchema } from "schemas/loginSchema"
import Copyright from "components/Common/copyright"
import {
  MerchantPortalLoginData,
  MerchantPortalService,
} from "services/MerchantPortalService"

type LoginFailureInput = {
  error?: unknown
  errorCode?: number | string
  message?: string
}

function extractErrorText(error: unknown): string {
  if (typeof error === "string") {
    return error
  }
  if (!error || typeof error !== "object") {
    return ""
  }
  const record = error as Record<string, unknown>
  if (typeof record.error === "string") {
    return record.error
  }
  if (typeof record.message === "string") {
    return record.message
  }
  return ""
}

function loginFailureMessage(res: LoginFailureInput): string {
  const code = String(res.errorCode ?? "").trim()
  const byCode: Record<string, string> = {
    MERCHANT_PORTAL_CREDENTIALS: "Invalid email or password.",
    MERCHANT_PORTAL_INACTIVE:
      "This account is inactive. Contact Zulu support to reactivate it.",
    MERCHANT_PORTAL_ROLE:
      "This portal is only for Shopify and custom-integrated merchants.",
    MERCHANT_PORTAL_NO_STORE:
      "No Shopify or custom store is linked to this account. Connect a store first, or sign in from your shop admin.",
    MERCHANT_PORTAL_SESSION:
      "Could not open the merchant dashboard for this store. Try again.",
    MERCHANT_PORTAL_FAILED:
      "Could not sign in to the merchant portal. Try again.",
    "1": "Verify your email before signing in to the merchant portal.",
  }
  if (byCode[code]) {
    return byCode[code]
  }

  const raw = extractErrorText(res.error) || String(res.message || "")
  const message = raw.replace(/\s+/g, " ").trim()

  if (
    !message ||
    /^network error$/i.test(message) ||
    /^request failed with status code/i.test(message) ||
    /^timeout/i.test(message) ||
    /^\[object /i.test(message)
  ) {
    return "Could not reach Zulu right now. Check your connection and try again."
  }

  if (
    /could not find a user/i.test(message) ||
    /incorrect email/i.test(message) ||
    /password combination/i.test(message) ||
    /invalid email or password/i.test(message)
  ) {
    return "Invalid email or password."
  }
  if (/currently inactive/i.test(message)) {
    return "This account is inactive. Contact Zulu support to reactivate it."
  }
  if (/verify your account/i.test(message)) {
    return "Verify your email before signing in to the merchant portal."
  }

  return message
}

const MerchantPortalLogin: React.FC = () => {
  const [loginRequestState, setLoginRequestState] = useState<RequestState>(RequestState.INITIAL)
  const [showPassword, setShowPassword] = useState(false)
  const [loginError, setLoginError] = useState("")

  const onSubmit = async (
    values: MerchantPortalLoginData,
    actions: { setSubmitting: (v: boolean) => void }
  ) => {
    setLoginError("")
    try {
      setLoginRequestState(RequestState.LOADING)
      const res = await MerchantPortalService.login(values)
      if (res.success && res.data?.redirectPath) {
        setLoginRequestState(RequestState.SUCCESS)
        window.location.replace(res.data.redirectPath)
        return
      }
      setLoginRequestState(RequestState.FAILED)
      setLoginError(loginFailureMessage(res))
    } catch (error) {
      setLoginRequestState(RequestState.FAILED)
      setLoginError(loginFailureMessage({ error }))
    } finally {
      actions.setSubmitting(false)
    }
  }

  const {
    values,
    errors,
    touched,
    isSubmitting,
    handleBlur,
    handleChange,
    setFieldValue,
    handleSubmit,
  } = useFormik({
    initialValues: {
      emailOrUserName: "",
      password: "",
      termsAndConditions: false,
    },
    validationSchema: loginSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit,
  })

  const onFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (loginError) {
      setLoginError("")
    }
    handleChange(event)
  }

  const handleCheckboxChange = () => {
    setFieldValue("termsAndConditions", !values.termsAndConditions)
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  document.title = "Zulu Merchant Portal"

  return (
    <div className="container">
      <form onSubmit={handleSubmit} autoComplete="on">
        <section className="my-4">
          <div className="position-relative">
            <div className="row g-5 w-100 justify-content-center sign-form">
              <div className="col-lg-7 col-md-12 col-sm-12 d-flex align-items-center justify-content-center">
                <img src={theme} alt="" className="w-100 px-5 py-5 position-relative" />
              </div>

              <div className="col-lg-5 col-md-12 col-sm-12 d-flex align-items-center justify-content-center">
                <div className="px-4 py-5 w-100">
                  <div className="sign-logo mb-3">
                    <a href="/home">
                      <img src={logo} alt="Zulu" />
                    </a>
                  </div>
                  <h3 className="title my-4 text-black">Zulu Merchant Portal</h3>
                  {loginError ? (
                    <div className="alert alert-danger" role="alert" aria-live="polite">
                      {loginError}
                    </div>
                  ) : null}
                  <p className="mb-4 text-muted">
                    Sign in with your shop email (username) and password. Your account must be
                    linked to a Shopify or custom-integrated store.
                  </p>
                  <div className="form-groupco mt-2">
                    <label className="place-order-title" htmlFor="emailOrUserName">
                      Shop email (username)
                    </label>
                    <input
                      value={values.emailOrUserName}
                      onChange={onFieldChange}
                      name="emailOrUserName"
                      id="emailOrUserName"
                      type="text"
                      inputMode="email"
                      autoComplete="username"
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck={false}
                      placeholder="e.g. orders@yourshop.com"
                      onBlur={handleBlur}
                      className={
                        errors.emailOrUserName && touched.emailOrUserName
                          ? "input-error form-control"
                          : "form-control"
                      }
                    />

                    {errors.emailOrUserName && touched.emailOrUserName && (
                      <label className="error place-order-title" htmlFor="emailOrUserName">
                        {errors.emailOrUserName}
                      </label>
                    )}
                  </div>
                  <div className="form-group mt-2">
                    <label className="place-order-title" htmlFor="password">
                      Password
                    </label>
                    <div className="password-input-container">
                      <input
                        value={values.password}
                        onChange={onFieldChange}
                        name="password"
                        id="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder=""
                        onBlur={handleBlur}
                        className={
                          errors.password && touched.password
                            ? "input-error form-control"
                            : "form-control"
                        }
                      />
                      {values.password && (
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={togglePasswordVisibility}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="bi bi-eye" viewBox="0 0 16 16">
                              <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
                              <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
                            </svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="currentColor" className="bi bi-eye-slash" viewBox="0 0 16 16">
                              <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
                              <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
                              <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                    {errors.password && touched.password && (
                      <label className="error place-order-title" htmlFor="password">
                        {errors.password}
                      </label>
                    )}
                  </div>

                  <div className="form-group mb-4">
                    <Link to="/forget-password">Forgot your password?</Link>
                  </div>

                  <div className="form-group">
                    <div className="row">
                      <div className="col-lg-12">
                        <input
                          checked={values.termsAndConditions}
                          className="me-2"
                          type="checkbox"
                          name="termsAndConditions"
                          id="termsAndConditions"
                          onChange={handleCheckboxChange}
                          onBlur={handleBlur}
                        />
                        <label className="place-order-title ms-2" htmlFor="termsAndConditions">
                          Remember Me
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <button
                      type="submit"
                      disabled={isSubmitting || loginRequestState === RequestState.LOADING}
                      className="btn btn-md bg-orange text-white w-100 mt-4 button-bg"
                    >
                      {loginRequestState === RequestState.LOADING ? "Signing in…" : "Log In"}
                    </button>
                  </div>

                  <div className="w-100 text-center mt-5">
                    <Copyright />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </form>
    </div>
  )
}

export default MerchantPortalLogin
